import { useMutation, useQuery } from '@apollo/client';
import { Button, DatePicker, Drawer, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { normalizeTimezone } from '../../../../utils/timezoneUtils';
import {
    CREATE_SORTING_BATCHES,
    CreateSortingBatchesResponse,
    CreateSortingBatchesVariables,
    GET_SORTING_BATCH_DESTINATIONS,
    GET_SORTING_BATCH_SERVER_NOW,
    GET_SORTING_BATCHES,
    GetSortingBatchDestinationsResponse,
    GetSortingBatchDestinationsVariables,
    GetSortingBatchServerNowResponse,
    GetSortingBatchServerNowVariables,
    GetSortingBatchesResponse,
    GetSortingBatchesVariables,
    SortingBatchDestinationType,
} from '../graphql/sortingBatch';
import { BatchStatus } from '../types';
import { destinationKeyOf, previewBatchNumbers, resolveHubPrefix } from '../utils/batchNameUtils';
import { SelectableChip } from './CustomCodeRuleEditor';
import { CreateBagsTarget, NewBatchDrawerProps } from './overlayProps';

dayjs.extend(utc);
dayjs.extend(timezone);

interface DestinationGroup {
    hub: string;
    /** 本 Hub：只能选其配送仓 */
    own: boolean;
    /** label 为空表示 Hub 本身（展示「Hub」） */
    items: { key: string; label: string | null }[];
}

const DRAWER_WIDTH = 480;
const BATCH_TIME_FORMAT = 'YYYY-MM-DD HH:mm';

/** 按 isOwnHub 分组：本 Hub 只列配送仓；其他 Hub 可选 Hub 本身或其配送仓 */
// warehouseCode 是完整的 Hub-Whs 分拣码（如 LAX-NW），选项只展示 whs 段（NW）；null 表示 Hub 本身
const whsSegmentLabel = (code?: string | null): string | null => (code ? code.split('-').pop()! : null);

const buildDestinationGroups = (destinations: SortingBatchDestinationType[]): DestinationGroup[] => {
    const own = destinations.filter((d) => d.isOwnHub);
    const others = destinations.filter((d) => !d.isOwnHub);
    const ownGroup: DestinationGroup[] = own.length
        ? [{
            hub: own[0].hubCode,
            own: true,
            items: own.map((d) => ({ key: destinationKeyOf(d.hubCode, d.warehouseCode), label: whsSegmentLabel(d.warehouseCode) })),
        }]
        : [];
    const otherHubCodes = Array.from(new Set(others.map((d) => d.hubCode)));
    const otherGroups: DestinationGroup[] = otherHubCodes.map((hub) => ({
        hub,
        own: false,
        items: others
            .filter((d) => d.hubCode === hub)
            .map((d) => ({ key: destinationKeyOf(d.hubCode, d.warehouseCode), label: whsSegmentLabel(d.warehouseCode) })),
    }));
    return [...ownGroup, ...otherGroups];
};

/** 默认目的地：本 Hub 全部配送仓 + 其他各 Hub 本身 */
const defaultDestinationKeys = (groups: DestinationGroup[]): string[] =>
    groups.flatMap((g) => (g.own ? g.items.map((it) => it.key) : [destinationKeyOf(g.hub)]));

const miniButtonClass = 'cursor-pointer border-0 bg-transparent p-0 text-[11.5px] text-ant-purple-6';

/**
 * 把服务端瞬时时刻换算到创建仓时区，作为批次时间默认值。
 *
 * 不取客户端时钟（`dayjs()`/`Date.now()`）：浏览器系统时间被改过（或时区与仓不一致）时，
 * 默认时间会直接错，且与后端按创建仓时区生成的真实批次名不一致。时区取值与后端
 * `BatchServiceImpl.createSortingBatches` 同口径，只认 `warehouse.timezone`；
 * 缺失/非法时兜底 UTC（而不是浏览器本地时区），避免兜底值本身又带回客户端时区偏差。
 *
 * 仓库表 timezone 有两种写法：IANA 时区名（如 America/Los_Angeles）走 `.tz()`；
 * `GMT-5`/`UTC-7` 这种固定偏移（后端 Java `ZoneId.of()` 认，JS `Intl` 不认）先经
 * `normalizeTimezone` 转成 `-05:00` 再走 `.utcOffset()`，同 pickup 审核池 hubTime.ts 的口径。
 * 仍识别不了时 `.tz()` 会抛异常，必须 try/catch，否则抽屉在 `useEffect` 里崩溃、白屏。
 */
const hubNow = (serverNow: string, tz: string | null | undefined): Dayjs => {
    const zoned = tz ? safeZoned(serverNow, tz) : null;
    return zoned && zoned.isValid() ? zoned : dayjs.utc(serverNow);
};

const safeZoned = (serverNow: string, tz: string): Dayjs | null => {
    try {
        return tz.includes('/') ? dayjs.utc(serverNow).tz(tz) : dayjs.utc(serverNow).utcOffset(normalizeTimezone(tz));
    } catch {
        return null;
    }
};

/** 新建批次抽屉：按目的地多选批量创建批次 */
export const NewBatchDrawer: React.FC<NewBatchDrawerProps> = ({ open, onClose, onCreated }) => {
    const { t } = useTranslation();
    const k = (key: string) => `wms.sortingBatch.newBatch.${key}`;
    const { currentWarehouse } = useWarehouseContext();
    const warehouseId = currentWarehouse?.id;
    const hubPrefix = resolveHubPrefix(currentWarehouse ? { sortingCode: currentWarehouse.sortingCode, name: currentWarehouse.name } : null);

    const { data: destinationsData } = useQuery<GetSortingBatchDestinationsResponse, GetSortingBatchDestinationsVariables>(
        GET_SORTING_BATCH_DESTINATIONS,
        { variables: { warehouseId: warehouseId || '0' }, skip: !open || !warehouseId, fetchPolicy: 'network-only' },
    );
    const groups = useMemo(() => buildDestinationGroups(destinationsData?.sortingBatchDestinations || []), [destinationsData]);

    // 批次时间默认值取服务端当前时刻（按创建仓时区换算），不取浏览器时钟；每次打开抽屉都重新取
    const { data: serverNowData } = useQuery<GetSortingBatchServerNowResponse, GetSortingBatchServerNowVariables>(
        GET_SORTING_BATCH_SERVER_NOW,
        { variables: { warehouseId: warehouseId || '0' }, skip: !open || !warehouseId, fetchPolicy: 'network-only' },
    );

    // 用于批次名查重；每次打开抽屉都取最新数据
    const { data: batchesData } = useQuery<GetSortingBatchesResponse, GetSortingBatchesVariables>(
        GET_SORTING_BATCHES,
        { variables: { warehouseId: warehouseId || '0' }, skip: !open || !warehouseId, fetchPolicy: 'network-only' },
    );
    const existingBatches = batchesData?.sortingBatches || [];

    const [createSortingBatches, { loading: creating }] = useMutation<CreateSortingBatchesResponse, CreateSortingBatchesVariables>(
        CREATE_SORTING_BATCHES,
    );

    const [selected, setSelected] = useState<string[]>([]);
    // null = 服务端时刻还没取回；不用浏览器时钟兜底占位，避免闪一下错误时间
    const [batchTime, setBatchTime] = useState<Dayjs | null>(null);

    // 打开时清空批次时间（不借用上一次打开的旧值），只认 open 这一次跳变；
    // 不能放进下面那个依赖 groups 的 effect——目的地树和服务端时刻是两个并发请求，谁后到不确定，
    // 一旦目的地树请求比服务端时刻请求晚返回，这个 effect 会在时间已经填好之后又把它清成 null，
    // 之后没有任何逻辑会再去重新填，批次时间就永久卡在空白（已回归验证的真实 bug）
    useEffect(() => {
        if (!open) return;
        setBatchTime(null);
    }, [open]);

    // 每次打开重置全部输入（同 wms/consolidation CreateConsolidationTaskDrawer 打开时 resetFields）：
    // 默认全选本 Hub 配送仓 + 其他 Hub
    useEffect(() => {
        if (!open) return;
        setSelected(defaultDestinationKeys(groups));
    }, [open, groups]);

    // 批次时间默认值：服务端当前时刻换算到创建仓时区，skip 随 open 复位保证每次打开都重新取一次
    useEffect(() => {
        if (!open || !serverNowData) return;
        const { serverNow, timezone: hubTimezone } = serverNowData.sortingBatchServerNow;
        setBatchTime(hubNow(serverNow, hubTimezone));
    }, [open, serverNowData]);

    // 批次名预览：传入已按创建仓时区标记的 Dayjs 对象（而非裸时间戳），与后端按创建仓时区生成的真实批次名口径一致；
    // 服务端时刻还没回来时不渲染任何预览，避免借用浏览器时钟临时拼出一个随后又会变化的批次名
    const previewNames = batchTime ? previewBatchNumbers(hubPrefix, batchTime.startOf('minute'), selected) : [];
    // 批次名唯一：与本仓已有发出批次（含已取消）重名的不可创建，真正的唯一性校验由后端兜底
    const existingNames = useMemo(() => new Set(existingBatches.map((b) => b.batchName)), [existingBatches]);
    const duplicateNames = previewNames.filter((name) => existingNames.has(name));
    const separator = t('wms.sortingBatch.common.separator');

    const toggle = (key: string) => setSelected((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));

    const selectGroup = (g: DestinationGroup) => {
        const keys = g.items.map((it) => it.key);
        setSelected((prev) => Array.from(new Set([...prev, ...keys])));
    };

    const clearGroup = (g: DestinationGroup) => {
        const keys = g.items.map((it) => it.key);
        setSelected((prev) => prev.filter((x) => !keys.includes(x)));
    };

    const handleCreate = async () => {
        if (!warehouseId || !batchTime) return;
        if (!selected.length) {
            message.warning(t(k('noDestination')));
            return;
        }
        if (duplicateNames.length) {
            message.warning(t(k('duplicateNames'), { names: duplicateNames.join(separator) }));
            return;
        }
        try {
            const { data } = await createSortingBatches({
                variables: {
                    input: {
                        warehouseId,
                        destinationKeys: selected,
                        batchTime: String(batchTime.startOf('minute').valueOf()),
                    },
                },
            });
            const result = data?.createSortingBatches;
            if (!result) return;
            message.success(t(k('created'), { count: result.batches.length }));
            onCreated(result.batches.map((b): CreateBagsTarget => ({
                id: Number(b.id),
                batchNumber: b.batchName,
                // destinationWhsCode 本身已经是完整的 Hub-Whs 两段码（如 "LAX-DC"），不能再拼一次 Hub
                destination: b.destinationWhsCode || b.destinationHubCode,
                status: b.status as unknown as BatchStatus,
            })));
        } catch (error: any) {
            // 全局 errorLink 对 BUSINESS_ERROR 只兜底两个特定 messageKey 弹窗，其余（如批次名重复）只打日志不弹窗，
            // 需要页面自己兜底提示，参照 SorterModal.tsx 同样的写法
            const messageKey = error?.graphQLErrors?.[0]?.extensions?.messageKey;
            message.error(messageKey ? (t(messageKey) as string) || messageKey : t(k('createFailed')));
        }
    };

    return (
        <Drawer
            open={open}
            title={t(k('title'))}
            width={DRAWER_WIDTH}
            onClose={onClose}
            footer={(
                <div className="flex justify-end gap-2">
                    <Button onClick={onClose}>{t(k('cancel'))}</Button>
                    <Button type="primary" loading={creating} disabled={!batchTime} onClick={handleCreate}>{t(k('create'))}</Button>
                </div>
            )}
        >
            <div className="flex flex-col gap-3.5">
                <div className="flex items-center gap-2.5">
                    <span className="text-[13px] font-medium">{t(k('destinations'))}</span>
                    <span className="flex-1 text-xs text-[#86909c]">{t(k('destinationsHint'))}</span>
                    <button type="button" className={`${miniButtonClass} text-xs`} onClick={() => setSelected([])}>
                        {t('wms.sortingBatch.common.clear')}
                    </button>
                </div>
                <div className="rounded border border-solid border-[#e5e6eb]">
                    {groups.map((g) => (
                        <div key={g.hub} className="flex items-start gap-2.5 border-0 border-b border-solid border-[#f2f3f5] px-3 py-2 last:border-b-0">
                            <span className="flex w-[92px] flex-none flex-col gap-0.5">
                                <span className="text-[12.5px] font-medium">{g.hub}</span>
                                <span className="flex gap-2">
                                    <button type="button" className={miniButtonClass} onClick={() => selectGroup(g)}>{t(k('selectAll'))}</button>
                                    <button type="button" className={miniButtonClass} onClick={() => clearGroup(g)}>
                                        {t('wms.sortingBatch.common.clear')}
                                    </button>
                                </span>
                                {g.own && <span className="text-[11px] text-[#c26b00]">{t(k('ownHubNote'))}</span>}
                            </span>
                            <span className="flex flex-1 flex-wrap gap-[5px]">
                                {g.items.map((it) => (
                                    <SelectableChip key={it.key} selected={selected.includes(it.key)} onClick={() => toggle(it.key)}>
                                        {it.label ?? t('wms.sortingBatch.destinationType.HUB')}
                                    </SelectableChip>
                                ))}
                            </span>
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-3">
                    <span className="w-24 flex-none text-[13px] text-[#4e5969]">{t(k('batchTime'))}</span>
                    <DatePicker
                        className="flex-1"
                        showTime={{ format: 'HH:mm' }}
                        format={BATCH_TIME_FORMAT}
                        placeholder={BATCH_TIME_FORMAT}
                        allowClear={false}
                        value={batchTime}
                        onChange={(v) => {
                            if (v) setBatchTime(v);
                        }}
                    />
                </div>
                <div className="rounded border border-solid border-[#f2f3f5] bg-[#f7f8fa] px-3.5 py-3 text-xs leading-[1.9] text-[#4e5969]">
                    {t(k('previewCount'), { count: previewNames.length })}
                    <div className="mt-1 flex flex-wrap gap-x-3 text-ant-purple-6">
                        {previewNames.map((name) => (existingNames.has(name)
                            ? (
                                <span key={name} className="text-[#cb2634]">
                                    <span>{name}</span>
                                    <span>{t(k('nameExists'))}</span>
                                </span>
                            )
                            : <span key={name}>{name}</span>))}
                    </div>
                </div>
            </div>
        </Drawer>
    );
};
